<template>
  <div id="cycle-chart">
    <VisualizeFilter />
    <div class="chart-container">
      <div :class="[lightMode ? 'chart-instructions' : 'chart-instructions dark-mode']">
        <small>
          💡 Hover over data points and press Cmd+C (Mac) or Ctrl+C (Windows/Linux) to copy data
        </small>
      </div>
      <highcharts
        ref="chart"
        class="chart"
        :key="`cycle-chart-${showCycle}`"
        :options="chartOptions"
      />
    </div>

    <!-- Cycle X-Ray Section - permanently visible -->
    <div v-if="!showFwView" class="xray-section">
      <h3 class="xray-title">
        Cycle X-Ray
        <span v-if="selectedCycleDetails" class="cycle-number-subtitle">
          (For Cycle
          {{
            cycleHoverState.isHovered
              ? cycleHoverState.cycleIndex + 1
              : (selectedCycleDetails.plotBandIndex || selectedCycleDetails.cycleIndex || 0) + 1
          }})
        </span>
      </h3>
      <div class="xray-content">
        <div v-if="selectedCycleDetails" class="cycle-details">
          <!-- Explanation Section -->
          <div v-if="selectedCycleDetails.explanation" class="explanation-section">
            <h5>Cycle Analysis</h5>
            <p class="explanation-text">{{ selectedCycleDetails.explanation }}</p>
          </div>

          <!-- Basic Info Section -->
          <CycleInfoGrid title="Basic Information" :items="basicInfoItems" />

          <!-- Temperature Data Section -->
          <CycleInfoGrid title="Temperature Data" :items="temperatureDataItems" />

          <!-- Supply Air Section -->
          <CycleAirDataSection
            v-if="selectedCycleDetails.supplyAir"
            title="Supply Air"
            :air-data="selectedCycleDetails.supplyAir"
          />

          <!-- Return Air Section -->
          <CycleAirDataSection
            v-if="selectedCycleDetails.returnAir"
            title="Return Air"
            :air-data="selectedCycleDetails.returnAir"
          />

          <!-- Attic Air Section -->
          <CycleAirDataSection
            v-if="selectedCycleDetails.atticAir"
            title="Attic Air"
            :air-data="selectedCycleDetails.atticAir"
          />

          <!-- Outdoor Air Section -->
          <CycleAirDataSection
            v-if="selectedCycleDetails.outdoorAir"
            title="Outdoor Air"
            :air-data="selectedCycleDetails.outdoorAir"
          />

          <!-- Triggers Section -->
          <CycleTriggersSection
            v-if="selectedCycleDetails.startTrigger || selectedCycleDetails.endTrigger"
            :start-trigger="selectedCycleDetails.startTrigger"
            :end-trigger="selectedCycleDetails.endTrigger"
          />

          <!-- Pressure Change Section -->
          <CyclePressureSection
            v-if="selectedCycleDetails.pressureChange"
            :pressure-change="selectedCycleDetails.pressureChange"
          />

          <!-- DT Payload Section -->
          <CycleInfoGrid
            v-if="selectedCycleDetails.dtPayload"
            title="Delta T Payload"
            :items="dtPayloadItems"
          />

          <!-- Tail Data Section -->
          <CycleTailDataSection
            v-if="selectedCycleDetails.tail"
            :tail="selectedCycleDetails.tail"
          />

          <!-- Decision Metrics Section -->
          <CycleInfoGrid
            v-if="selectedCycleDetails.decisionMetrics"
            title="Decision Metrics"
            :items="decisionMetricsItems"
          />

          <!-- Additional Fields Section -->
          <CycleInfoGrid title="Additional Information" :items="additionalInfoItems" />

          <!-- Raw JSON Section -->
          <RawJsonDisplay :cycle-details="selectedCycleDetails" />
        </div>
        <div v-else class="no-selection">
          <p>Click on a cycle to see its details here</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { mapGetters, mapState } from 'vuex';

import deviceDetailMixin from '@shared/mixins/deviceDetailMixin';
import VisualizeFilter from '@shared/components/deviceDetail/cycleChart/VisualizeFilter.vue';
import CycleInfoGrid from '@shared/components/deviceDetail/cycleChart/components/CycleInfoGrid.vue';
import CycleAirDataSection from '@shared/components/deviceDetail/cycleChart/components/CycleAirDataSection.vue';
import CycleTriggersSection from '@shared/components/deviceDetail/cycleChart/components/CycleTriggersSection.vue';
import CyclePressureSection from '@shared/components/deviceDetail/cycleChart/components/CyclePressureSection.vue';
import CycleTailDataSection from '@shared/components/deviceDetail/cycleChart/components/CycleTailDataSection.vue';
import RawJsonDisplay from '@shared/components/deviceDetail/cycleChart/components/RawJsonDisplay.vue';
import { useChartInteractions } from '@shared/components/deviceDetail/cycleChart/utils/useChartInteractions.js';
import { useCopyFunctionality } from '@shared/components/deviceDetail/cycleChart/utils/useCopyFunctionality.js';

export default {
  name: 'CycleChart',
  components: {
    VisualizeFilter,
    CycleInfoGrid,
    CycleAirDataSection,
    CycleTriggersSection,
    CyclePressureSection,
    CycleTailDataSection,
    RawJsonDisplay,
  },
  mixins: [deviceDetailMixin],
  props: {
    legendVisibilityState: {
      type: Object,
      default: () => ({}),
    },
    lightMode: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      componentRef: null,
    };
  },
  computed: {
    ...mapGetters(['cycleChartData']),
    ...mapState({
      loading: (state) => state.deviceDetail.loadingCycleChart,
      showFwView: (state) => state.deviceDetail.showFwView,
      showCycle: (state) => state.deviceDetail.showCycle,
      allSupplySerialCycles: (state) => state.deviceDetail.allSupplySerialCycles,
      selectedSupplySerial: (state) => state.deviceDetail.selectedSupplySerial,
      partnerView: (state) => state.deviceDetail.partnerView,
      timezone: (state) => state.deviceDetail.timezone,
      timezoneFormat: (state) => state.deviceDetail.timezoneFormat,
      hub: (state) => state.deviceDetail.hub,
    }),

    chartInteractions() {
      if (!this.cycleChartData) return null;

      const key = `${this.cycleChartData?.length}-${this.allSupplySerialCycles?.length}-${this.selectedSupplySerial}-${this.partnerView}-${this.showCycle}-${this.showFwView}-${this.timezone}-${this.timezoneFormat}-${this.hub?.tzOffset}`;

      return useChartInteractions(
        this.cycleChartData,
        this.allSupplySerialCycles,
        this.selectedSupplySerial,
        this.partnerView,
        this.showCycle,
        this.showFwView,
        ref(this.timezone),
        ref(this.timezoneFormat),
        ref(this.hub?.tzOffset),
      );
    },

    copyFunctionality() {
      if (!this.chartInteractions) return null;

      return useCopyFunctionality(this.chartInteractions.datetime, this.cycleChartData);
    },

    selectedCycleDetails: {
      get() {
        return this.chartInteractions?.selectedCycleDetails?.value || null;
      },
      set(value) {
        if (this.chartInteractions?.selectedCycleDetails)
          this.chartInteractions.selectedCycleDetails.value = value;
      },
    },
    cycleHoverState: {
      get() {
        return (
          this.chartInteractions?.cycleHoverState?.value || {
            isHovered: false,
            cycleIndex: null,
            cycleData: null,
          }
        );
      },
      set(value) {
        if (this.chartInteractions?.cycleHoverState)
          this.chartInteractions.cycleHoverState.value = value;
      },
    },
    selectedCycleIndex: {
      get() {
        return this.chartInteractions?.selectedCycleIndex?.value ?? null;
      },
      set(value) {
        if (this.chartInteractions?.selectedCycleIndex)
          this.chartInteractions.selectedCycleIndex.value = value;
      },
    },
    copySuccess: {
      get() {
        return this.copyFunctionality?.copySuccess?.value || false;
      },
      set(value) {
        if (this.copyFunctionality?.copySuccess) this.copyFunctionality.copySuccess.value = value;
      },
    },

    datetime() {
      return this.chartInteractions?.datetime?.value || [];
    },
    returnTemp() {
      return this.chartInteractions?.returnTemp?.value || [];
    },
    supplyTemp() {
      return this.chartInteractions?.supplyTemp?.value || [];
    },
    atticTemp() {
      return this.chartInteractions?.atticTemp?.value || [];
    },
    outsideTemp() {
      return this.chartInteractions?.outsideTemp?.value || [];
    },
    humidity() {
      return this.chartInteractions?.humidity?.value || [];
    },
    filterPressure() {
      return this.chartInteractions?.filterPressure?.value || [];
    },
    dataset() {
      return this.chartInteractions?.dataset?.value || {};
    },
    dataSeries() {
      return this.chartInteractions?.dataSeries?.value || [];
    },
    cycleData() {
      return this.chartInteractions?.cycleData?.value || [];
    },
    actualCycleData() {
      return this.chartInteractions?.actualCycleData?.value || [];
    },
    chartOptions() {
      return this.chartInteractions?.chartOptions?.value || {};
    },

    basicInfoItems() {
      if (!this.selectedCycleDetails) return [];

      return [
        { label: 'Device ID', value: this.selectedCycleDetails.uid, type: 'text' },
        {
          label: 'Start Time',
          value: this.formatTimezone(this.selectedCycleDetails.startTime, this.partnerView),
          type: 'text',
        },
        {
          label: 'End Time',
          value: this.formatTimezone(this.selectedCycleDetails.endTime, this.partnerView),
          type: 'text',
        },
        { label: 'Duration', value: this.selectedCycleDetails.cycleTimeSeconds, type: 'duration' },
        {
          label: 'AC Mode',
          value: this.selectedCycleDetails.acMode,
          type: 'badge',
          valueClass: `mode-badge ${this.selectedCycleDetails.acMode?.toLowerCase()}`,
        },
        {
          label: 'Cycle Speed',
          value: this.selectedCycleDetails.cycleSpeed || this.selectedCycleDetails.speed || 'N/A',
          type: 'text',
        },
        {
          label: 'Status',
          value: this.selectedCycleDetails.valid ? 'Valid' : 'Invalid',
          type: 'status',
          valueClass: `status-badge ${this.selectedCycleDetails.valid ? 'valid' : 'invalid'}`,
        },
        {
          label: 'Finished',
          value: this.selectedCycleDetails.finished ? 'Yes' : 'No',
          type: 'status',
          valueClass: `status-badge ${this.selectedCycleDetails.finished ? 'valid' : 'invalid'}`,
        },
        {
          label: 'Update Time',
          value: this.formatTimezone(this.selectedCycleDetails.updateTime, this.partnerView),
          type: 'text',
        },
      ];
    },

    temperatureDataItems() {
      if (!this.selectedCycleDetails) return [];

      return [
        { label: 'Delta T', value: this.selectedCycleDetails.deltaT, type: 'temperature' },
        { label: 'Outdoor Temp', value: this.selectedCycleDetails.tempAPI, type: 'temperature' },
        { label: 'Outdoor RH', value: this.selectedCycleDetails.rhAPI, type: 'humidity' },
      ];
    },

    dtPayloadItems() {
      if (!this.selectedCycleDetails?.dtPayload) return [];

      return [
        {
          label: 'Timestamp',
          value: this.formatTimezone(
            this.selectedCycleDetails.dtPayload.timestamp,
            this.partnerView,
          ),
          type: 'text',
        },
        {
          label: 'Supply Temp',
          value: this.selectedCycleDetails.dtPayload.sup,
          type: 'temperature',
        },
        {
          label: 'Return Temp',
          value: this.selectedCycleDetails.dtPayload.ret,
          type: 'temperature',
        },
      ];
    },

    decisionMetricsItems() {
      if (!this.selectedCycleDetails?.decisionMetrics) return [];

      const isValueNotCalculated = (value) =>
        value === null || value === undefined || isNaN(value) || value === 0;

      return [
        {
          label: 'Calculated',
          value: this.selectedCycleDetails.decisionMetrics.calculated ? 'Yes' : 'No',
          type: 'status',
          valueClass: `status-badge ${this.selectedCycleDetails.decisionMetrics.calculated ? 'valid' : 'invalid'}`,
        },
        {
          label: 'Considered Payload Count',
          value: isValueNotCalculated(
            this.selectedCycleDetails.decisionMetrics.consideredPayloadCount,
          )
            ? 'Not Calculated'
            : this.selectedCycleDetails.decisionMetrics.consideredPayloadCount,
          type: 'status',
          valueClass: isValueNotCalculated(
            this.selectedCycleDetails.decisionMetrics.consideredPayloadCount,
          )
            ? 'status-badge not-calculated'
            : '',
        },
        {
          label: 'Harmonizing',
          value: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.harmonizing)
            ? 'Not Calculated'
            : this.selectedCycleDetails.decisionMetrics.harmonizing,
          type: 'status',
          valueClass: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.harmonizing)
            ? 'status-badge not-calculated'
            : '',
        },
        {
          label: 'High Delta',
          value: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.highDelta)
            ? 'Not Calculated'
            : `${this.selectedCycleDetails.decisionMetrics.highDelta} ${this.getDeltaUnit('highDelta')}`,
          type: 'status',
          valueClass: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.highDelta)
            ? 'status-badge not-calculated'
            : '',
        },
        {
          label: 'High Temperature',
          value: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.highTemperature)
            ? 'Not Calculated'
            : `${this.selectedCycleDetails.decisionMetrics.highTemperature}°F`,
          type: 'status',
          valueClass: isValueNotCalculated(
            this.selectedCycleDetails.decisionMetrics.highTemperature,
          )
            ? 'status-badge not-calculated'
            : '',
        },
        {
          label: 'Insignificant Temp Slope',
          value: isValueNotCalculated(
            this.selectedCycleDetails.decisionMetrics.insignificantTemperatureSlope,
          )
            ? 'Not Calculated'
            : this.selectedCycleDetails.decisionMetrics.insignificantTemperatureSlope,
          type: 'status',
          valueClass: isValueNotCalculated(
            this.selectedCycleDetails.decisionMetrics.insignificantTemperatureSlope,
          )
            ? 'status-badge not-calculated'
            : '',
        },
        {
          label: 'Low Delta',
          value: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.lowDelta)
            ? 'Not Calculated'
            : `${this.selectedCycleDetails.decisionMetrics.lowDelta} ${this.getDeltaUnit('lowDelta')}`,
          type: 'status',
          valueClass: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.lowDelta)
            ? 'status-badge not-calculated'
            : '',
        },
        {
          label: 'Low Temperature',
          value: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.lowTemperature)
            ? 'Not Calculated'
            : `${this.selectedCycleDetails.decisionMetrics.lowTemperature}°F`,
          type: 'status',
          valueClass: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.lowTemperature)
            ? 'status-badge not-calculated'
            : '',
        },
        {
          label: 'Cooling DT Count',
          value: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.coolingDtCount)
            ? 'Not Calculated'
            : this.selectedCycleDetails.decisionMetrics.coolingDtCount,
          type: 'status',
          valueClass: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.coolingDtCount)
            ? 'status-badge not-calculated'
            : '',
        },
        {
          label: 'Heating DT Count',
          value: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.heatingDtCount)
            ? 'Not Calculated'
            : this.selectedCycleDetails.decisionMetrics.heatingDtCount,
          type: 'status',
          valueClass: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.heatingDtCount)
            ? 'status-badge not-calculated'
            : '',
        },
        {
          label: 'Cooling RH Count',
          value: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.coolingRhCount)
            ? 'Not Calculated'
            : this.selectedCycleDetails.decisionMetrics.coolingRhCount,
          type: 'status',
          valueClass: isValueNotCalculated(this.selectedCycleDetails.decisionMetrics.coolingRhCount)
            ? 'status-badge not-calculated'
            : '',
        },
        {
          label: 'Cooling Comfort Temp Count',
          value: isValueNotCalculated(
            this.selectedCycleDetails.decisionMetrics.coolingComfortTempCount,
          )
            ? 'Not Calculated'
            : this.selectedCycleDetails.decisionMetrics.coolingComfortTempCount,
          type: 'status',
          valueClass: isValueNotCalculated(
            this.selectedCycleDetails.decisionMetrics.coolingComfortTempCount,
          )
            ? 'status-badge not-calculated'
            : '',
        },
      ];
    },

    additionalInfoItems() {
      if (!this.selectedCycleDetails) return [];

      const items = [];
      if (this.selectedCycleDetails.thermostatStat) {
        items.push({
          label: 'Thermostat Status',
          value: this.selectedCycleDetails.thermostatStat,
          type: 'text',
        });
      }
      if (this.selectedCycleDetails.detector) {
        items.push({ label: 'Detector', value: this.selectedCycleDetails.detector, type: 'text' });
      }
      if (this.selectedCycleDetails.setpoint) {
        items.push({ label: 'Setpoint', value: this.selectedCycleDetails.setpoint, type: 'text' });
      }
      if (this.selectedCycleDetails.tag) {
        items.push({ label: 'Tag', value: this.selectedCycleDetails.tag, type: 'text' });
      }
      if (this.selectedCycleDetails.thermostat) {
        items.push({
          label: 'Thermostat',
          value: this.selectedCycleDetails.thermostat,
          type: 'text',
        });
      }
      if (this.selectedCycleDetails.rooms) {
        items.push({ label: 'Rooms', value: this.selectedCycleDetails.rooms, type: 'text' });
      }

      return items;
    },
  },
  watch: {
    showFwView(value) {
      if (value) {
        this.showCycle = false;
      }
    },
  },
  mounted() {
    if (this.partnerView) {
      this.showCycle = false;
    }
    this.componentRef = this;
    window.cycleChartComponent = this;

    this.copyFunctionality.addCopyEventListener.call(this);

    this.$nextTick(() => {
      if (this.$refs.chart && this.$refs.chart.chart) {
        this.$refs.chart.chart.vueComponent = this;
      }
    });
  },
  beforeDestroy() {
    this.copyFunctionality.removeCopyEventListener.call(this);
  },
  methods: {
    getDeltaUnit(logic) {
      if (!logic) return '°F';

      const logicStr = logic.toString().toLowerCase();

      if (
        logicStr.includes('pressure') ||
        logicStr.includes('press') ||
        logicStr.includes('pa') ||
        logicStr.includes('filter') ||
        logicStr.includes('flow')
      ) {
        return 'Pa';
      }

      if (
        logicStr.includes('temp') ||
        logicStr.includes('temperature') ||
        logicStr.includes('delta') ||
        logicStr.includes('cooling') ||
        logicStr.includes('heating')
      ) {
        return '°F';
      }

      return '°F';
    },

    isValueNotCalculated(value) {
      return value === null || value === undefined || isNaN(value) || value === 0;
    },

    getDateRangeText() {
      if (!this.datetime || this.datetime.length === 0) return '';

      const startDate = new Date(this.datetime[0]);
      const endDate = new Date(this.datetime[this.datetime.length - 1]);

      const startStr = startDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });

      const endStr = endDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });

      if (startStr === endStr) {
        return startStr;
      }

      return `${startStr} - ${endStr}`;
    },

    // Legend management methods for parent component communication
    handleLegendItemClick(series, event, chart) {
      const result = this.chartInteractions.handleLegendItemClick(
        series,
        event,
        chart,
        this.legendVisibilityState,
      );

      // Emit event to parent component with the updated state
      this.$emit('legend-visibility-changed', {
        seriesName: result.seriesName,
        visible: result.visible,
        visibilityState: result.visibilityState,
      });
    },

    applyLegendVisibilityState(chart) {
      this.chartInteractions.applyLegendVisibilityState(chart, this.legendVisibilityState);
    },

    resetLegendVisibility() {
      const newState = this.chartInteractions.resetLegendVisibility(this.$refs.chart?.chart);

      // Emit event to parent to reset state
      this.$emit('legend-visibility-changed', {
        seriesName: 'all',
        visible: true,
        visibilityState: newState,
      });
    },

    getLegendVisibilityState() {
      return { ...this.legendVisibilityState };
    },

    setLegendVisibilityState(state) {
      this.chartInteractions.applyLegendVisibilityState(this.$refs.chart?.chart, state);
    },

    // Copy functionality methods
    copyTooltipData() {
      this.copyFunctionality.copyTooltipData.call(this);
    },

    copyDataPointsToClipboard(points) {
      this.copyFunctionality.copyDataPointsToClipboard.call(this, points);
    },

    copyToClipboard(text) {
      this.copyFunctionality.copyToClipboard.call(this, text);
    },

    showCopySuccess() {
      this.copyFunctionality.showCopySuccess.call(this);
    },

    showCopyError(message) {
      this.copyFunctionality.showCopyError.call(this, message);
    },

    // Chart interaction methods
    updateCycleHoverState(cycleIndex, cycleData) {
      this.chartInteractions.updateCycleHoverState(cycleIndex, cycleData);
    },

    clearCycleHoverState() {
      this.chartInteractions.clearCycleHoverState();
    },

    handlePlotBandClick(plotBand, index, chart) {
      this.chartInteractions.handlePlotBandClick(plotBand, index, chart);
    },

    handlePlotBandHover(plotBand, index, chart) {
      this.chartInteractions.handlePlotBandHover(plotBand, index, chart);
    },

    handlePlotBandMouseOut(plotBand, chart) {
      this.chartInteractions.handlePlotBandMouseOut(plotBand, chart);
    },

    // Utility methods
    getRandomBrightColor() {
      return this.chartInteractions.getRandomBrightColor();
    },

    formatDuration(seconds) {
      if (!seconds) return 'N/A';
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    },

    getTailDataCount() {
      if (!this.selectedCycleDetails?.tail) {
        return 0;
      }

      const supplyCount = this.selectedCycleDetails.tail.supplys
        ? this.selectedCycleDetails.tail.supplys.length
        : 0;
      const returnCount = this.selectedCycleDetails.tail.returns
        ? this.selectedCycleDetails.tail.returns.length
        : 0;

      return Math.max(supplyCount, returnCount);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './styles/cycleChartShared.scss';
</style>
